/**
 * Terms Acceptance Checkbox Component - Ellis Reed
 * A checkbox with transparent modal overlay for terms and privacy policy acceptance
 */

"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Shield, ExternalLink, Download } from "lucide-react";
import { cn } from "@/lib/utils";

interface TermsAcceptanceCheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  required?: boolean;
  className?: string;
  showModal?: boolean; // Whether to show modal on first render
}

export function TermsAcceptanceCheckbox({
  checked,
  onCheckedChange,
  required = true,
  className,
  showModal = false,
}: TermsAcceptanceCheckboxProps) {
  const [isModalOpen, setIsModalOpen] = useState(showModal);

  const handleCheckboxClick = () => {
    if (!checked) {
      // Show modal when trying to check
      setIsModalOpen(true);
    } else {
      // Allow unchecking without modal
      onCheckedChange(false);
    }
  };

  const handleAccept = () => {
    onCheckedChange(true);
    setIsModalOpen(false);
  };

  const handleDecline = () => {
    onCheckedChange(false);
    setIsModalOpen(false);
  };

  return (
    <>
      <div className={cn("flex items-start space-x-3", className)}>
        <Checkbox
          id="terms-acceptance"
          checked={checked}
          onCheckedChange={handleCheckboxClick}
          className="mt-1"
        />
        <Label
          htmlFor="terms-acceptance"
          className="cursor-pointer select-none text-sm leading-relaxed"
        >
          I agree to the{" "}
          <Link
            href="/terms"
            target="_blank"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
            <ExternalLink className="h-3 w-3" />
          </Link>{" "}
          and{" "}
          <Link
            href="/privacy"
            target="_blank"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
            <ExternalLink className="h-3 w-3" />
          </Link>
          {required && <span className="ml-1 text-destructive">*</span>}
        </Label>
      </div>

      {/* Transparent Overlay Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl border-2 bg-background/95 backdrop-blur-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <FileText className="h-6 w-6 text-primary" />
              Terms & Privacy Agreement
            </DialogTitle>
            <DialogDescription className="pt-2 text-base">
              Please review and accept our terms of service and privacy policy
              to continue.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Terms of Service Section */}
            <div className="rounded-lg border bg-gradient-to-br from-blue-50 to-blue-100 p-4 dark:from-blue-950/30 dark:to-blue-900/20">
              <div className="mb-3 flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500 text-white">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-lg font-semibold">
                    Terms of Service
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    By using USLand, you agree to our terms including acceptable
                    use, user responsibilities, and service limitations.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Link
                  href="/terms"
                  target="_blank"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  Read Full Terms of Service
                  <ExternalLink className="h-4 w-4" />
                </Link>
                <Link
                  href="/terms-of-use.pdf"
                  target="_blank"
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:underline dark:text-blue-300"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </Link>
              </div>
            </div>

            {/* Privacy Policy Section */}
            <div className="rounded-lg border bg-gradient-to-br from-green-50 to-green-100 p-4 dark:from-green-950/30 dark:to-green-900/20">
              <div className="mb-3 flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-500 text-white">
                  <Shield className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-lg font-semibold">Privacy Policy</h3>
                  <p className="text-sm text-muted-foreground">
                    We respect your privacy and are committed to protecting your
                    personal data. Learn how we collect, use, and safeguard your
                    information.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Link
                  href="/privacy"
                  target="_blank"
                  className="inline-flex items-center gap-2 text-sm font-medium text-green-600 hover:underline dark:text-green-400"
                >
                  Read Full Privacy Policy
                  <ExternalLink className="h-4 w-4" />
                </Link>
                <Link
                  href="/privacy-policy.pdf"
                  target="_blank"
                  className="inline-flex items-center gap-2 text-sm font-medium text-green-700 hover:underline dark:text-green-300"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </Link>
              </div>
            </div>

            {/* Key Points */}
            <div className="rounded-lg border bg-muted/50 p-4">
              <h4 className="mb-3 font-semibold">Key Points:</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-primary">•</span>
                  <span>
                    Your data is protected with industry-standard security
                    measures
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-primary">•</span>
                  <span>
                    We never sell your personal information to third parties
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-primary">•</span>
                  <span>You can request deletion of your data at any time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-primary">•</span>
                  <span>You must be 18 years or older to use this service</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse gap-3 border-t pt-4 sm:flex-row">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="flex-1"
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
            >
              I Accept
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground">
            By clicking &quot;I Accept&quot;, you acknowledge that you have read
            and agree to our Terms of Service and Privacy Policy
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
